import "./Sidebar.scss";

import clsx from "clsx";
import _ from "lodash";
import React, { useEffect, useLayoutEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { useOrganizationContext } from "../../contexts/organizations/organizationsContext";
import type { IBillingInfo } from "../../hooks/reactQueryHooks/useBilling";
import { BillingBadgePtu, BillingBadgeRegular } from "./Badges";
import { MobileSidebar } from "./MobileSidebar";
import { SidebarLogo } from "./SidebarLogo";
import { SidebarNav } from "./SidebarNav";

interface SideBarProps {
  billingData: IBillingInfo | undefined;
}

const Sidebar: React.FC<SideBarProps> = ({ billingData }) => {
  const [show, setShow] = useState(true);
  const [hovered, setHovered] = useState(false);

  const { pathname } = useLocation();

  // should be removed after backend fixes the issue
  const navigate = useNavigate();

  const hideItems =
    billingData?.provider === "microsoft" && billingData?.type === "Regular";

  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  const [screenResolution, setScreenResolution] = useState<
    "desktop" | "middle" | "mobile"
  >("desktop");

  // should be removed after backend fixes the issue
  useEffect(() => {
    if (hideItems) {
      if (pathname === "/overview" || pathname === "/playground") {
        navigate("/error/404");
      }
    }
  }, [pathname, hideItems, navigate]);

  useEffect(() => {
    setHovered(false);
    if (screenResolution !== "desktop") {
      setShow(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const { isOwner } = useOrganizationContext();

  const openMobileSidebar = () => {
    setShowMobileSidebar(true);
  };
  const closeMobileSidebar = () => {
    setShowMobileSidebar(false);
  };
  useEffect(() => {
    window.addEventListener("resize", () => {
      setScreenResolution(getScreenResolution());
      if (getScreenResolution() === "desktop") {
        setShow(true);
        setHovered(false);
        closeMobileSidebar();
        return;
      }
      if (getScreenResolution() === "middle") {
        setShow(false);
        setHovered(false);
        closeMobileSidebar();
        return;
      }
    });
  }, []);

  const getScreenResolution = () => {
    if (window.innerWidth < 992) {
      return "mobile";
    }
    if (window.innerWidth <= 1440) {
      return "middle";
    }
    return "desktop";
  };

  useLayoutEffect(() => {
    const screenType = getScreenResolution();
    setScreenResolution(screenType);
    if (screenType === "middle") {
      setShow(false);
      setHovered(false);
    }
  }, []);

  const sidebarClassName = clsx("react-sidebar", {
    "react-sidebar--show": show,
    middle: screenResolution === "middle",
  });
  const sidebarContainerClassName = clsx("react-sidebar-container", {
    "react-sidebar-container--hovered": hovered,
    middle: screenResolution === "middle",
  });

  const toggleSidebar = () => {
    setShow(!show);
  };
  const handleMouseEnter = () => {
    setHovered(true);
  };
  const handleMouseLeave = () => {
    if (!show) {
      setHovered(false);
    }
  };
  if (screenResolution === "mobile") {
    return (
      <MobileSidebar
        openMobileSidebar={openMobileSidebar}
        closeMobileSidebar={closeMobileSidebar}
        showMobileSidebar={showMobileSidebar}
        showUsageLink={!!billingData?.ptuBillingInfo}
        isOwner={isOwner}
        hideItems={hideItems}
      />
    );
  }

  return (
    <div
      onMouseEnter={handleMouseEnter}
      className={sidebarClassName}
      onMouseLeave={handleMouseLeave}
    >
      <div className={sidebarContainerClassName}>
        <SidebarLogo
          isMinimized={!show}
          hovered={hovered}
          showNav={toggleSidebar}
        />
        <div className="sidebar-nav-container">
          <SidebarNav
            showUsageLink={!!billingData?.ptuBillingInfo}
            isOwner={isOwner}
            hideItems={hideItems}
          />
        </div>

        {!_.isEmpty(billingData) && (
          <div className="react-sidebar-billing-container">
            {billingData.ptuBillingInfo && (
              <BillingBadgePtu
                billingData={billingData.ptuBillingInfo}
                isStripe={billingData.provider === "stripe"}
              />
            )}

            {billingData.regularBillingInfo && (
              <BillingBadgeRegular
                billingData={billingData.regularBillingInfo}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export { Sidebar };
